import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: SignUpData) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

interface SignUpData {
  full_name: string;
  birth_date: string;
  gender: string;
  weight: string;
  height: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check account status after successful login
      if (data.user) {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("account_status")
          .eq("id", data.user.id)
          .single();

        if (profileError) {
          console.error("Error checking account status:", profileError);
        } else if (profile) {
          // If account is deactivated, reactivate it
          if (profile.account_status === "deactivated") {
            await supabase
              .from("profiles")
              .update({
                account_status: "active",
                account_deactivated_at: null,
              })
              .eq("id", data.user.id);
            
            toast.success("Welcome back! Your account has been reactivated.");
            navigate("/discover");
            return;
          }
          
          // If account is deleted or scheduled for deletion, prevent login
          if (profile.account_status === "deleted" || profile.account_status === "deletion_scheduled") {
            await supabase.auth.signOut();
            toast.error("This account has been deleted and cannot be accessed.");
            throw new Error("Account deleted");
          }
        }
      }

      toast.success("Login successful!");
      navigate("/discover");
    } catch (error: any) {
      toast.error(error.message || "Error signing in");
      throw error;
    }
  };

  const signUp = async (email: string, password: string, userData: SignUpData) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: userData,
      },
    });

    if (error) {
      // Handle specific error messages
      if (error.message.includes("already registered") || error.message.includes("duplicate")) {
        toast.error("This email is already registered. Please sign in instead.");
      } else {
        toast.error(error.message || "Error creating account");
      }
      throw error;
    }

    // Check if email confirmation is required
    if (data.user && !data.session) {
      toast.success("Account created! Please check your email to verify your account.");
    } else if (data.session) {
      toast.success("Account created successfully!");
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast.success("Signed out successfully!");
      navigate("/");
    } catch (error: any) {
      toast.error(error.message || "Error signing out");
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) throw error;

      toast.success("Recovery email sent!");
    } catch (error: any) {
      toast.error(error.message || "Error sending email");
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
